/**
 * JSON ↔ in-memory codecs for persistence.
 *
 * Decimal serializes to a string ("0.05"), bigint to a string ("100000000"),
 * Date to ISO string. On parse, we round-trip back through their constructors.
 */

import { z } from 'zod';
import { Decimal } from '../engine/money.js';

export const decimalSchema = z.union([z.string(), z.number()]).transform(v => new Decimal(v));
export const bigintStringSchema = z.string().transform(v => BigInt(v));
export const isoDateSchema = z.string().transform(v => new Date(v));

export const purchaseSchema = z.object({
  id: z.string(),
  date: isoDateSchema,
  usd: decimalSchema,
  btc: decimalSchema,
  note: z.string().nullable().optional(),
  category: z.enum([
    'salary', 'bonus', 'tax_refund', 'stock_sale', 'profit_share',
    'loan', 'manual', 'other',
  ]),
  source: z.enum(['actual', 'planned']),
  scenarioId: z.string().optional(),
});

export const goalSchema = z.object({
  id: z.string(),
  label: z.string(),
  targetBtc: decimalSchema,
  enabled: z.boolean().optional().default(true),
});

export const planEntrySchema = z.object({
  date: isoDateSchema,
  usd: decimalSchema,
  btc: decimalSchema,
  btcPrice: decimalSchema,
  note: z.string().optional(),
  category: z.enum([
    'salary', 'bonus', 'tax_refund', 'stock_sale', 'profit_share',
    'loan', 'manual', 'other',
  ]).optional(),
});

export const planSchema = z.object({
  entries: z.array(planEntrySchema),
  finalBtcBalance: decimalSchema,
});

export const seedSchema = z.object({
  extractedAt: z.string(),
  sourceWorkbook: z.string(),
  priceModel: z.object({
    increaseInTimeToDouble: decimalSchema,
    initialDoublingPeriodDays: decimalSchema,
    startDate: isoDateSchema,
    startPriceUsd: decimalSchema,
    stepDays: decimalSchema,
  }),
  goals: z.array(z.object({
    id: z.string(),
    label: z.string(),
    targetBtc: decimalSchema,
  })),
  actualPurchases: z.array(z.object({
    date: isoDateSchema,
    usd: decimalSchema,
    btc: decimalSchema,
    category: z.string(),
    source: z.literal('actual'),
    note: z.string().nullable(),
  })),
  conservativePlan: planSchema,
  hopefulPlan: planSchema,
  loopLoan: z.unknown(),
  carry: z.unknown(),
});

export type SeedData = z.infer<typeof seedSchema>;

/** Encode an in-memory app state to a JSON-safe object for LocalStorage.
 *
 *  Tagging:
 *    Decimal → "dec:1.5"
 *    bigint  → "bi:100000000"
 *    Date    → "dt:2026-05-01T..."
 *    other strings, numbers, booleans, arrays, objects pass through.
 *
 *  Tagging Decimals (added in M6) closes a roundtrip leak from M2-M5 where
 *  Decimals were saved as bare strings and not recognized on reload. Old-format
 *  LocalStorage payloads (where Decimals are bare strings) decode as strings
 *  and are silently dropped by the store's `instanceof Decimal` checks  -  same
 *  failure mode as before, no regression. After one save with the new tagging,
 *  state persists correctly forever after.
 */
export function encodeForStorage<T>(value: T): unknown {
  if (value === null || value === undefined) return value;
  if (value instanceof Decimal) return `dec:${value.toString()}`;
  if (typeof value === 'bigint') return `bi:${value.toString()}`;
  if (value instanceof Date) return `dt:${value.toISOString()}`;
  if (Array.isArray(value)) return value.map(encodeForStorage);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) out[k] = encodeForStorage(v);
    return out;
  }
  return value;
}

/** Decode a JSON-loaded object back to in-memory types (Decimal/bigint/Date). */
export function decodeFromStorage(value: unknown): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    if (value.startsWith('bi:')) return BigInt(value.slice(3));
    if (value.startsWith('dt:')) return new Date(value.slice(3));
    if (value.startsWith('dec:')) return new Decimal(value.slice(4));
    return value;
  }
  if (Array.isArray(value)) return value.map(decodeFromStorage);
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = decodeFromStorage(v);
    }
    return out;
  }
  return value;
}
